import React, { useMemo } from "react";
import { ChatReference } from "@src/features/chat/ChatReference";
import { ChatUrlReference } from "@src/features/chat/ChatUrlReference";
import type {
  FileCitationAnnotation,
  UrlCitationAnnotation,
} from "@src/features/chat/types";
import { useChatReferences } from "@src/features/aiAssist/hooks/useChatReferences";
import type { OpusAgentTurnFragment } from "./useOpusAgentChat.generated";

export type OpusAgentCitation = OpusAgentTurnFragment["citations"][number];

// file_citation cards key off the library item id; intercom_article cards
// key off the url. Citations without a key are skipped, mirroring
// MessageAnnotations.
const getCitationLookupId = (citation: OpusAgentCitation): string => {
  if (citation.type === "file_citation") return citation.libraryItemId ?? "";
  return citation.url ?? "";
};

const toFileCitationAnnotation = (
  citation: OpusAgentCitation,
): FileCitationAnnotation => ({
  type: "file_citation",
  file_id: citation.libraryItemId ?? "",
  filename: citation.title ?? "",
  quote: citation.quote ?? undefined,
  index: null,
  metadata: { library_item_id: citation.libraryItemId },
});

const toUrlCitationAnnotation = (
  citation: OpusAgentCitation,
): UrlCitationAnnotation => ({
  type: "intercom_article",
  file_id: citation.url ?? "",
  filename: citation.title ?? "",
  index: null,
  metadata: {
    url: citation.url ?? "",
    title: citation.title ?? undefined,
  },
});

interface OpusAgentCitationsProps {
  citations: OpusAgentCitation[];
}

// The same citation cards Ask Opus renders, grouped/numbered the way
// MessageAnnotations does, adapted to the OpusAgentCitation GraphQL shape.
export const OpusAgentCitations: React.FC<OpusAgentCitationsProps> = ({
  citations,
}) => {
  const supportedCitations = useMemo(
    () =>
      citations.filter(
        (citation) =>
          citation.type === "file_citation" ||
          citation.type === "intercom_article",
      ),
    [citations],
  );

  const { uniqueCitations, citationCounts, citationNumberMap } = useMemo(() => {
    const uniqueMap = new Map<string, OpusAgentCitation>();
    const counts: Record<string, number> = {};
    supportedCitations.forEach((citation) => {
      const key = getCitationLookupId(citation);
      if (!key) return;
      if (!uniqueMap.has(key)) uniqueMap.set(key, citation);
      counts[key] = (counts[key] || 0) + 1;
    });
    const numberMap = new Map<string, number>();
    [...uniqueMap.keys()].forEach((key, index) => {
      numberMap.set(key, index + 1);
    });
    return {
      uniqueCitations: [...uniqueMap.values()],
      citationCounts: counts,
      citationNumberMap: numberMap,
    };
  }, [supportedCitations]);

  const libraryItemIds = useMemo(
    () =>
      Array.from(
        new Set(
          uniqueCitations
            .filter((citation) => citation.type === "file_citation")
            .map((citation) => citation.libraryItemId)
            .filter((id): id is string => !!id),
        ),
      ),
    [uniqueCitations],
  );

  const { references, loading: referencesLoading } =
    useChatReferences(libraryItemIds);

  const libraryItemsByLookupId = useMemo(
    () => new Map(references.map((ref) => [ref.lookupId, ref.libraryItem])),
    [references],
  );

  if (uniqueCitations.length === 0) return null;

  return (
    <div className="mt-2 flex flex-col gap-2">
      {referencesLoading
        ? uniqueCitations.map((citation, idx) => (
            <div
              key={getCitationLookupId(citation) || idx}
              className="flex items-center rounded-lg border border-gray-6 bg-white p-3 shadow-sm"
            >
              <div className="size-6 animate-pulse rounded bg-gray-3" />
              <div className="ml-3 flex-1">
                <div className="h-4 w-32 animate-pulse rounded bg-gray-3" />
                <div className="mt-1 h-3 w-24 animate-pulse rounded bg-gray-3" />
              </div>
            </div>
          ))
        : uniqueCitations.map((citation) => {
            const lookupId = getCitationLookupId(citation);
            const count = citationCounts[lookupId] || 1;
            const citationNumber = citationNumberMap.get(lookupId);
            return (
              <div
                key={`opus-agent-citation-${lookupId}`}
                className="rounded-lg border border-border shadow-sm transition-shadow duration-200 hover:shadow-md"
              >
                {citation.type === "file_citation" ? (
                  <ChatReference
                    annotation={toFileCitationAnnotation(citation)}
                    libraryItem={libraryItemsByLookupId.get(lookupId)}
                    referenceCount={count}
                    citationNumber={citationNumber}
                  />
                ) : (
                  <ChatUrlReference
                    annotation={toUrlCitationAnnotation(citation)}
                    referenceCount={count}
                    citationNumber={citationNumber}
                  />
                )}
              </div>
            );
          })}
    </div>
  );
};
